import { useEffect, useMemo, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { OutwriteApi } from '../services/OutwriteService';

function optionKey(modelId, mode) {
  return `${modelId}::${mode}`;
}

/**
 * Runtime LLM model/mode switcher for Outwrite pages.
 */
export default function OutwriteLlmModelSelect({ disabled = false, sx }) {
  const [models, setModels] = useState([]);
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([OutwriteApi.listLlmModels(), OutwriteApi.getLlmSettings()])
      .then(([modelList, settings]) => {
        if (cancelled) return;
        const list = Array.isArray(modelList) ? modelList : [];
        setModels(list);
        if (settings?.modelId && settings?.mode) {
          setValue(optionKey(settings.modelId, settings.mode));
        } else if (list[0]) {
          setValue(optionKey(list[0].id, list[0].mode));
        }
      })
      .catch(() => {
        // Global error modal via axios interceptor / errorBus
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(
    () =>
      models.map((m) => ({
        key: optionKey(m.id, m.mode),
        label: m.label || `${m.id}/${m.mode}`,
        modelId: m.id,
        mode: m.mode,
      })),
    [models],
  );

  const handleChange = (event) => {
    const next = event.target.value;
    const match = items.find((item) => item.key === next);
    if (!match) return;
    setValue(next);
    OutwriteApi.setLlmSettings(match.modelId, match.mode).catch(() => {
      // revert on failure by reloading settings
      OutwriteApi.getLlmSettings()
        .then((settings) => {
          if (settings?.modelId && settings?.mode) {
            setValue(optionKey(settings.modelId, settings.mode));
          }
        })
        .catch(() => {});
    });
  };

  return (
    <FormControl size="small" sx={{ minWidth: 280, ...sx }} disabled={disabled || loading}>
      <InputLabel id="outwrite-llm-model-label">LLM model</InputLabel>
      <Select
        labelId="outwrite-llm-model-label"
        id="outwrite-llm-model"
        label="LLM model"
        value={value}
        onChange={handleChange}
      >
        {items.map((item) => (
          <MenuItem key={item.key} value={item.key}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
